import { InventoryRequestEmailEditForm } from "application/inventory-request/forms";
import { BaseEditViewStore } from "core/stores";
import { applicationContext } from "core/utils";
import { action, computed, observable } from "mobx";
import { InventoryRequestService } from "application/inventory-request/services";
import { ModalParams } from 'core/models';
import { isEmpty } from "lodash";
import { FacilityInventoryCountDetailService } from "../../facility-inventory/services";
import { FacilityOrderService, FacilityOrderStatusService, FacilityOrderTemplateService } from "../../facility-order/services";
import _ from 'lodash';
import { LocalPatientService } from "../../patient/services";
import { LookupService } from "common/services";
import { PersonService } from 'application/person/services';
import { ScheduleService } from 'application/schedule/services';
import { NikoPatientTemplateService } from "application/niko-patient/services";

@applicationContext
class InventoryRequestEmailEditViewStore extends BaseEditViewStore {
    @observable accessor body = null;
    @observable accessor isLoading = null;
    @observable accessor fetchingData = null;
    @observable accessor isManuallyLoading = null;

    acceptedFiles = ".csv,.pdf";
    @observable accessor fileCount = 0;
    @observable.ref accessor selectedFiles = null;
    @observable accessor isUploadInProgress = false;
    @observable accessor totalFileSize = 0;
    @observable accessor isInventoryRequestSent = false;
    @observable accessor emailSender = null;
    @observable accessor emailSentInfo = '';
    @observable accessor facilityOrder;
    @observable accessor facilityOrderStatus;
    @observable accessor isReviewed;
    @observable accessor isNiko = false;

    @computed
    get isDisabled() {
        return (
            !this.selectedFiles ||
            this.isUploadInProgress
        );
    }

    constructor(rootStore) {
        const inventoryRequestService = new InventoryRequestService(rootStore.application.baasic.apiClient);
        const facilityInventoryCountDetailService = new FacilityInventoryCountDetailService(rootStore.application.baasic.apiClient);
        const facilityOrderService = new FacilityOrderService(rootStore.application.baasic.apiClient);
        const facilityOrderId = rootStore.routerStore.routerState.params.facilityOrderId;
        const inventoryRequestType = rootStore.routerStore.routerState.params.inventoryRequestType;
        super(rootStore, {
            name: "inventory-request-email-edit",
            Form: InventoryRequestEmailEditForm,
            actions: () => {},
            FormClass: InventoryRequestEmailEditForm,
        });

        this.inventoryRequestService = inventoryRequestService;
        this.facilityInventoryCountDetailService = facilityInventoryCountDetailService;
        this.facilityOrderService = facilityOrderService;
        this.facilityOrderId = facilityOrderId;
        this.inventoryRequestType = inventoryRequestType
        this.getEmailPrefillData(facilityOrderId, inventoryRequestType);
        this.facilityOrderTemplateService = new FacilityOrderTemplateService(rootStore.application.baasic.apiClient);
        this.nikoPatientTemplateService = new NikoPatientTemplateService(rootStore.application.baasic.apiClient);
        this.localPatientService = new LocalPatientService(rootStore.application.baasic.apiClient);
        this.lookupService = new LookupService(rootStore.application.baasic.apiClient);

        this.loggedUserId = rootStore.userStore.applicationUser.id;
        this.uploadDocumentModal = new ModalParams({});
        this.sendManuallyModal = new ModalParams({});
        this.sendManuallyModal.onConfirm = async () => { await this.sendManualEmail(); };
        this.sendManuallyModal.onCancel = () => { this.sendManuallyConfirmationModal.close() };
        this.sendManuallyModal.onConfirm = async () => { await this.sendManualEmail(); };
        this.sendManuallyModal.onCancel = () => { this.sendManuallyConfirmationModal.close() };

        this.isUSICompleted = false;
        this.isNiko = rootStore.routerStore.routerState.queryParams.isNiko;
        this.isNiko = this.isNiko === 'true';
    }

    @action.bound
    downloadSingleFile(fileForm) {
        const selected = this.selectedFiles.find(f => f.index === fileForm.index);
        if (!selected || !(selected.file instanceof Blob)) {
            return;
        }

        const blobUrl = window.URL.createObjectURL(selected.file);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = fileForm.fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
    }

    @action.bound
    async getEmailPrefillData(facilityOrderId, inventoryRequestType) {
        try {
            this.fetchingData = true;
            this.facilityOrder = (await this.facilityOrderService.get(facilityOrderId, { embed: 'Facility,Facility.FacilityAreaOfCares,Facility.Corporate_,FacilityOrderStatuses' })).data;
            const response = this.isNiko ?
            await this.inventoryRequestService.getNikoEmailPrefillData(facilityOrderId, inventoryRequestType) :
            await this.inventoryRequestService.getEmailPrefillData(facilityOrderId, inventoryRequestType);
            this.lookups = await this.lookupService.getLookups("ItemCategory");
            this.enteralItemCategoryId = this.lookups.itemCategory.find((s) => s.abrv === 'enteral').id;

            await this.getOrderStatus();
            this.isUSICompleted = this.facilityOrderStatus && this.facilityOrderStatus.startedOn != null && this.facilityOrderStatus.completedOn != null;

            if(!this.isNiko)
            {
                const masterPatient = (await this.localPatientService.getByPatientKey(this.facilityOrder.facility.masterPatientId, { fields: 'id' })).data;
                this.masterPatientId = masterPatient ? masterPatient.id : undefined;
            }

            this.isInventoryRequestSent = response.data.isInventoryRequestSent;
            if(this.isInventoryRequestSent)
            {
                const sentEmailResponse = (await this.facilityOrderService.getSentEmailInfo(facilityOrderId, "InventoryRequest")).data;

                if (sentEmailResponse) {
                    const formattedDate = new Date(sentEmailResponse.eventDate).toLocaleString("en-US", {
                        month: "2-digit",
                        day: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        hour12: true
                      });

                    this.emailSentInfo = `Sent by ${sentEmailResponse.name} on ${formattedDate}`;
                } else {
                    this.emailSentInfo = '';
                }
            }
            this.form.update(response.data);
            if (inventoryRequestType == "Regular")
            {
                const fileDetails = await this.downloadInventoryDetail({ facilityOrderId });
                const file = new File([fileDetails.content], fileDetails.fileName);
                this.onFileDrop({ affectedFiles: [file] });
            }
            this.body = response.data.body;
            this.rootStore.titleStore.setTitle(response.data.facilityName ? `Inventory request - ${response.data.facilityName}` : 'Inventory request' );
            this.fetchingData = false;
        }
        catch (err) {
            let errorMessage = typeof err.data === "object" ? err.data.errorMessage : err.data;
            this.rootStore.notificationStore.error(errorMessage ? errorMessage : "DOCUMENTATION_REQUEST.ERROR_EMAIL_PREFILL");
            this.fetchingData = false;
        }
    }

    async downloadInventoryDetail(filter) {
        return new Promise(async (resolve, reject) => {
            let partUrl = this.isNiko ? await this.facilityInventoryCountDetailService.downloadNikoInventoryDetail(filter)
            :
            await this.facilityInventoryCountDetailService.downloadInventoryDetail(filter);

            const url = this.rootStore.application.baasic.apiClient.appOptions.apiUrl +
                        partUrl +
                        `&contentDisposition=attachment&token=${
                            this.rootStore.authStore.token.tokenUrl
                        }` +
                        `&ttype=${this.rootStore.authStore.token.type}`;

            const xhr = new XMLHttpRequest();
            xhr.open('GET', url, true);
            xhr.responseType = 'blob';

            xhr.onload = function () {
                if (xhr.status === 200) {
                    let fileName = 'download';
                    const disposition = xhr.getResponseHeader('Content-Disposition') || xhr.getResponseHeader('content-disposition');
                    if (disposition) {
                        const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^";\n]+)/i);
                        if (match && match[1]) {
                            fileName = decodeURIComponent(match[1]);
                        }
                    }

                    resolve({
                        fileName,
                        content: xhr.response,
                    });
                } else {
                    reject(new Error(`Download failed with status ${xhr.status}`));
                }
            };

            xhr.onerror = function () {
                reject(new Error('Network error during download'));
            };

            xhr.send();
        });
    }

    @action.bound
    async sendEmail() {
        try {
            const emailType =
                this.isNiko ?
                (await this.inventoryRequestService.checkIsNikoInventoryRequestEligible(this.facilityOrderId)).data :
                (await this.inventoryRequestService.checkIsInventoryRequestEligible(this.facilityOrderId)).data;
            this.hideInventoryEmailButton =
                this.isNiko ?
                (await this.inventoryRequestService.checkSharedNikoInventoryEmailConditions(this.facilityOrderId)).data :
                (await this.inventoryRequestService.checkSharedInventoryEmailConditions(this.facilityOrderId)).data;

            if(this.hideInventoryEmailButton || emailType == "None")
            {
                this.rootStore.notificationStore.error('Inventory Request not eligible for sending.');
                return;
            }

            this.isLoading = true;
            const resource = this.form.values();

            let formData = new FormData();
            if (this.selectedFiles){
                const totalSize = this.selectedFiles.reduce((sum, file) => sum + file.size, 0);
                if (totalSize > 20 * 1024 * 1024){ // 20MB
                    this.rootStore.notificationStore.error(`Total file size exceeds limit of 20MB`);
                    this.isLoading = false;
                    return;
                }

                for (const file of this.selectedFiles) {
                    formData.append("file", file.file, file.file.name);
                }
            }

            resource.files = null;
            resource.body = resource.body.replace(
                /<p>/gi,
                '<p style="font-family: Calibri; font-size: 11pt;">'
              );
            resource.body = this.encodeUnicodeToBase64(resource.body);
            formData.append("data", JSON.stringify({ ...resource, facilityOrderId: this.facilityOrderId }));

            await this.inventoryRequestService.sendEmail(formData);
            if (!this.isInventoryRequestSent)
            {
                await this.facilityOrderService.markInventoryReportAsSent(this.facilityOrderId, "InventoryRequest");
            }
            else
            {
                this.isInventoryRequestSent = true;
            }

            await this.getOrderStatus();
            if (!this.isUSICompleted && this.isReviewed)
            {
                await this.openClinicalReviewPage();
                this.rootStore.notificationStore.success("US Intake successfully completed.");
            }

            this.isLoading = false;
            this.isUSICompleted = this.facilityOrderStatus && this.facilityOrderStatus.startedOn != null && this.facilityOrderStatus.completedOn != null;
            this.rootStore.notificationStore.success("Email successfully sent.");
        }
        catch (err) {
            this.isLoading = false;
            let errorMessage = typeof err.data === "object" ? err.data.errorMessage : err.data;
            this.rootStore.notificationStore.error(errorMessage ? errorMessage : "DOCUMENTATION_REQUEST.ERROR_EMAIL_SEND");
        }
    }

    @action.bound
    async signalRAction(message) {
        if (message.table == "SE") {
            if (message.column == "EmailType" && message.id == this.facilityOrderId)
            {
                if (message.text == "InventoryRequest")
                {
                    this.isInventoryRequestSent = true;
                    if (this.emailSentInfo == '')
                    {
                        const sentEmailResponse = (await this.facilityOrderService.getSentEmailInfo(this.facilityOrderId, "InventoryRequest")).data;

                        if (sentEmailResponse) {
                            const formattedDate = new Date(sentEmailResponse.eventDate).toLocaleString("en-US", {
                                month: "2-digit",
                                day: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                                hour12: true
                            });

                            this.emailSentInfo = `Sent by ${sentEmailResponse.name} on ${formattedDate}`;
                        } else {
                            this.emailSentInfo = '';
                        }
                    }
                }
            }
        }
    }

    @action.bound
    async openClinicalReviewPage() {
        if (this.isUSICompleted || !this.isReviewed)
            return;

        const personService = new PersonService(this.rootStore.application.baasic.apiClient);
        const person = (await personService.getByUser(this.rootStore.userStore.user.id)).data;

        const scheduleService = new ScheduleService(this.rootStore.application.baasic.apiClient);
        let params = {
            pageSize: 1,
            calendarTypeId: this.orderingTypeId,
            facilityOrderId: this.facilityOrder.id,
            documentRepId: person.id,
            fields: "id"
        }
        const response = await scheduleService.find(params);
        if (response.data && response.data.item && response.data.item.some(i => i)) {
            if(this.isNiko){
                window.open(this.rootStore.routerStore.findRoute("master.app.main.niko-facility-order.clinical-review-preview").pattern.replace(":id", this.facilityOrder.id));
            }
            else{
                window.open(this.rootStore.routerStore.findRoute("master.app.main.facility-order.facility-order-new-flow.clinical-preview").pattern.replace(":id", this.facilityOrder.id));
            }
        }
    }

    @action.bound
    async sendManualEmail() {
        this.isManuallyLoading = true;
        const emailType =
            this.isNiko ?
            (await this.inventoryRequestService.checkIsNikoInventoryRequestEligible(this.facilityOrderId)).data :
            (await this.inventoryRequestService.checkIsInventoryRequestEligible(this.facilityOrderId)).data;
        this.hideInventoryEmailButton =
            this.isNiko ?
            (await this.inventoryRequestService.checkSharedNikoInventoryEmailConditions(this.facilityOrderId)).data :
            (await this.inventoryRequestService.checkSharedInventoryEmailConditions(this.facilityOrderId)).data;

        if(this.hideInventoryEmailButton || emailType == "None")
        {
            this.rootStore.notificationStore.error('Inventory Request not eligible for sending.');
            return;
        }
        await this.facilityOrderService.markInventoryReportAsSent(this.facilityOrderId, "InventoryRequest")
        this.isInventoryRequestSent = true;

        await this.getOrderStatus();
        if (!this.isUSICompleted && this.isReviewed)
        {
            await this.openClinicalReviewPage();
            this.rootStore.notificationStore.success("US Intake successfully completed.");
        }
        this.sendManuallyModal.close();
        this.isUSICompleted = this.facilityOrderStatus && this.facilityOrderStatus.startedOn != null && this.facilityOrderStatus.completedOn != null;
        this.isManuallyLoading = false;
    }

    async getOrderStatus(){
        const facilityOrderStatusService = new FacilityOrderStatusService(this.rootStore.application.baasic.apiClient);
        var statusesResponse = await facilityOrderStatusService.find({ facilityOrderId: this.facilityOrder.id, embed: "Status,StartedByUser,CompletedByUser" });
        this.facilityOrder.facilityOrderStatuses = statusesResponse.data && statusesResponse.data.item;
        this.facilityOrderStatus = this.facilityOrder.facilityOrderStatuses.find(i => i.status.abrv === 'us-intake');

        if (this.isNiko) {
            const nikoParams = {
                facilityOrderId: this.facilityOrderId,
                isUsIntakeReviewed: false,
                pageSize: 1,
                fields: 'id',
            };
            const nikoResponse = await this.nikoPatientTemplateService.find(nikoParams);
            this.isReviewed = !nikoResponse.data.item.some(x => x.id);
            return;
        }

        let params = {
            pageSize: 1,
            facilityOrderId: this.facilityOrderId,
            isReviewed: false,
            fields: "id"
        }

        if (this.facilityOrder.isEnteralWeek) {
            params = _.extend(params, {
                itemCategoryIds: [this.enteralItemCategoryId],
                //masterPatientId: this.masterPatientId
            });
        }

        const response = await this.facilityOrderTemplateService.find(params);
        this.isReviewed = response.data.item && !response.data.item.some(i => i);
    }

    closePage() {
        window.close();
    }

    @action.bound
    async download() {
        const { notificationStore } = this.rootStore;

        const resource = this.form.values();
        var fileName = resource.subject + '.msg';

        var xhr = new XMLHttpRequest();
        xhr.open('POST', `${this.inventoryRequestService.getDownloadUrl()}?contentDisposition=attachment&token=${
            this.rootStore.authStore.token.tokenUrl
        }&ttype=${
            this.rootStore.authStore.token.type}`, true);

        xhr.setRequestHeader('Asccept', '*/*');

        xhr.responseType = 'arraybuffer'; // this is required

        xhr.onload = function() {
            if (this.status == 200) {
                var blob = new Blob([this.response], { type: "application/octet-stream"});
                var link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = fileName;
                link.click();
                link.remove();
                // in case the Blob uses a lot of memory
                setTimeout(() => URL.revokeObjectURL(link.href), 7000);
            }
            else{
                notificationStore.error("Error occured while trying to download email.");
            }
        };

        let formData = new FormData();
        if (this.selectedFiles){
            const totalSize = this.selectedFiles.reduce((sum, file) => sum + file.size, 0);
                if (totalSize > 20 * 1024 * 1024){ // 20MB
                    this.rootStore.notificationStore.error(`Total file size exceeds limit of 20MB`);
                    return;
                }

            for (const file of this.selectedFiles) {
                formData.append("file", file.file, file.file.name);
            }
        }

        // create a JSON object
        const json = {
            userId: this.loggedUserId,
            body: this.encodeUnicodeToBase64(resource.body),
            cc: resource.cc,
            to: resource.to,
            replyTo: resource.replyTo,
            subject: resource.subject
        }
        formData.append("data", JSON.stringify(json));
        xhr.send(formData);

        if(!this.isInventoryRequestSent){
            this.rootStore.notificationStore.warning('If sending the email manually, please click on the “Sent Manually” button to confirm that you’ve done so and complete the flow.');
        }
    }

    @action.bound
    async openUploadDocumentPopup() {
        this.uploadDocumentModal.open();
    }

    @action.bound
    async openSendManuallyPopup() {
        this.sendManuallyModal.open();
    }

    @action.bound
    onFileDrop({ affectedFiles }) {
        if (!this.selectedFiles) this.selectedFiles = [];

        let formFields = this.form.$('files').value || [];

        affectedFiles.forEach(file => {
            const rawFile = typeof file.getRawFile === 'function' ? file.getRawFile() : file;

            const idx = this.fileCount++;
            this.selectedFiles.push({ index: idx, file: rawFile });

            formFields.push({
                index: idx,
                fileName: rawFile.name,
                fileSize: this.truncateToTwoDecimals(rawFile.size / 1024 / 1024),
                name: rawFile.name.split('.').slice(0, -1).join('.')
            });
        });

        this.updateTotalFileSize();
        this.form.$('files').value = formFields;
    }

    @action.bound
    removeAttachment(index) {
        let formFields = this.form.$('files').value;
        formFields = formFields.filter(item => item.index !== index);
        this.selectedFiles.splice(index, 1); // remove one element from index
        this.updateTotalFileSize();

        if (isEmpty(this.selectedFiles)) this.selectedFiles = null;

        this.fileCount = 0;
        formFields.forEach(item => { item.index = this.fileCount++ });

        this.form.$('files').value = formFields;
    }

    @action
    encodeUnicodeToBase64(str) {
        const bytes = new TextEncoder().encode(str);
        let binary = '';
        bytes.forEach(byte => binary += String.fromCharCode(byte));
        return btoa(binary);
    }

    updateTotalFileSize() {
        this.totalFileSize = this.truncateToTwoDecimals(
            this.selectedFiles.reduce((sum, entry) => sum + entry.file.size, 0) / 1024 / 1024
        );
    }

    truncateToTwoDecimals(number) {
        return Math.floor(number * 100) / 100;
      }

}

export default InventoryRequestEmailEditViewStore;
